import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import {
  buildSearchQuery,
  dateValue,
  QueryFieldMapping,
} from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";

export interface AdminsViewForm {
  readonly userName: string;
  readonly firstName: string;
  readonly middleName: string;
  readonly lastName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly emailConfirmed: boolean;
  readonly phoneNumber: string;
}

// Admins have no company / department / role / asset — the columns the other grids carry for those are
// deliberately omitted here. Last Login is kept so Admins can see which of their peers signed in recently.
export const adminsViewColumns: DataColumn[] = [
  {
    title: "User Name",
    key: "userName",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "First Name",
    key: "firstName",
    width: 192,
    sort: true,
  },
  {
    title: "Middle Name",
    key: "middleName",
    width: 192,
    sort: true,
  },
  {
    title: "Last Name",
    key: "lastName",
    width: 192,
    sort: true,
  },
  {
    title: "Date of Birth",
    key: "dateOfBirth",
    width: 128,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Email",
    key: "email",
    width: 256,
    sort: true,
  },
  {
    title: "Email Confirmed",
    key: "emailConfirmed",
    width: 128,
    sort: true,
  },
  {
    title: "Phone",
    key: "phoneNumber",
    width: 192,
    sort: true,
  },
  {
    title: "Last Login",
    key: "lastLoginAt",
    width: 144,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
  // Admin requirement 2026-11-03.
  {
    title: "Status",
    key: "isActive",
    width: 110,
    sort: true,
    transform: (v: boolean) => (v === false ? "Inactive" : "Active"),
  },
  {
    title: "Activated On",
    key: "activatedAt",
    width: 148,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Deactivated On",
    key: "deactivatedAt",
    width: 148,
    format: "DD/MM/YYYY",
    sort: true,
  },
];

const adminsQueryMappings: QueryFieldMapping[] = [
  { formField: "userName" },
  { formField: "firstName" },
  { formField: "middleName" },
  { formField: "lastName" },
  { formField: "dateOfBirth", operation: QueryOperation.Equal, transform: dateValue },
  { formField: "email" },
  { formField: "emailConfirmed", operation: QueryOperation.Equal },
  { formField: "phoneNumber" },
];

export const mapAdminsViewQuery = (values: AdminsViewForm): SearchQuery =>
  buildSearchQuery(values, adminsQueryMappings);

export const adminsSearchSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        { type: "textbox", name: "userName", label: "User Name" },
        { type: "textbox", name: "firstName", label: "First Name" },
        { type: "textbox", name: "middleName", label: "Middle Name" },
        { type: "textbox", name: "lastName", label: "Last Name" },
        { type: "datepicker", name: "dateOfBirth", label: "Date of Birth" },
        { type: "textbox", name: "email", label: "Email" },
        { type: "togglebox", name: "emailConfirmed", label: "Email Confirmed" },
        { type: "textbox", name: "phoneNumber", label: "Phone" },
      ],
    },
  ],
};
